import { useState, useRef, useEffect } from 'react'
import type { ModuleComponentProps } from '@/lib/module-registry'
import type { VaultModuleData, VaultEntry } from '@/types/task'

// ─── helpers ─────────────────────────────────────────────────────────────────

function entryKey(env: string, repo: string | null): string {
  return repo != null ? `${env}::${repo}` : env
}

function hasEntriesForEnv(entries: Record<string, VaultEntry[]>, env: string): boolean {
  return Object.entries(entries).some(
    ([k, rows]) => (k === env || k.startsWith(`${env}::`)) && rows.length > 0
  )
}

function hasEntriesForRepo(entries: Record<string, VaultEntry[]>, repo: string): boolean {
  return Object.entries(entries).some(
    ([k, rows]) => k.endsWith(`::${repo}`) && rows.length > 0
  )
}

// ─── component ───────────────────────────────────────────────────────────────

export function VaultInputComponent({
  value,
  onChange,
  readOnly,
}: ModuleComponentProps<'vault'>) {
  const envs    = value?.envs    ?? []
  const repos   = value?.repos   ?? []
  const entries = value?.entries ?? {}
  const hasRepos = repos.length > 0

  const [activeEnv, setActiveEnv] = useState<string>(() => envs[0] ?? '')
  // keyed by `${cellKey}_${index}`
  const [revealed, setRevealed] = useState<Record<string, boolean>>({})

  const [addingEnv, setAddingEnv]   = useState(false)
  const [addingRepo, setAddingRepo] = useState(false)
  const [newEnvVal, setNewEnvVal]   = useState('')
  const [newRepoVal, setNewRepoVal] = useState('')

  const envInputRef  = useRef<HTMLInputElement>(null)
  const repoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (addingEnv)  envInputRef.current?.focus()  }, [addingEnv])
  useEffect(() => { if (addingRepo) repoInputRef.current?.focus() }, [addingRepo])

  useEffect(() => {
    if (envs.length > 0 && !envs.includes(activeEnv)) setActiveEnv(envs[0])
  }, [envs, activeEnv])

  // ── update helpers ──────────────────────────────────────────────────────────

  function updateValue(patch: Partial<VaultModuleData>) {
    onChange({ envs, repos, entries, ...patch } as VaultModuleData)
  }

  function addEnv() {
    const name = newEnvVal.trim()
    if (!name || envs.includes(name)) return
    updateValue({ envs: [...envs, name] })
    setActiveEnv(name)
    setNewEnvVal('')
    setAddingEnv(false)
  }

  function removeEnv(env: string) {
    if (hasEntriesForEnv(entries, env)) return
    updateValue({ envs: envs.filter((e) => e !== env) })
  }

  function addRepo() {
    const name = newRepoVal.trim()
    if (!name || repos.includes(name)) return
    updateValue({ repos: [...repos, name] })
    setNewRepoVal('')
    setAddingRepo(false)
  }

  function removeRepo(repo: string) {
    if (hasEntriesForRepo(entries, repo)) return
    updateValue({ repos: repos.filter((r) => r !== repo) })
  }

  function getCellRows(env: string, repo: string | null): VaultEntry[] {
    return entries[entryKey(env, repo)] ?? []
  }

  function setCellRows(env: string, repo: string | null, rows: VaultEntry[]) {
    const key  = entryKey(env, repo)
    const next = { ...entries, [key]: rows }
    if (rows.length === 0) delete next[key]
    updateValue({ entries: next })
  }

  function addVar(env: string, repo: string | null) {
    setCellRows(env, repo, [...getCellRows(env, repo), { key: '', value: '' }])
  }

  function updateVar(env: string, repo: string | null, index: number, field: 'key' | 'value', val: string) {
    const rows = getCellRows(env, repo).map((r, i) => i === index ? { ...r, [field]: val } : r)
    setCellRows(env, repo, rows)
  }

  function removeVar(env: string, repo: string | null, index: number) {
    setCellRows(env, repo, getCellRows(env, repo).filter((_, i) => i !== index))
  }

  function toggleReveal(cellKey: string, index: number) {
    const id = `${cellKey}_${index}`
    setRevealed((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  // ── empty state ─────────────────────────────────────────────────────────────

  if (!value || envs.length === 0) {
    if (readOnly) return <span className="text-sm text-muted-foreground">—</span>
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Tambah environment terlebih dahulu.</p>
        <div className="flex flex-wrap items-center gap-2">
          {addingEnv ? (
            <input
              ref={envInputRef}
              value={newEnvVal}
              onChange={(e) => setNewEnvVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addEnv() }
                if (e.key === 'Escape') { setAddingEnv(false); setNewEnvVal('') }
              }}
              placeholder="misal: staging"
              className="h-6 rounded border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddingEnv(true)}
              className="flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
            >
              + Tambah Environment
            </button>
          )}
        </div>
      </div>
    )
  }

  // ── read-only mode ──────────────────────────────────────────────────────────

  if (readOnly) {
    return (
      <div className="space-y-3">
        {/* Env tabs */}
        <div className="border-b border-border">
          <div className="flex gap-1">
            {envs.map((env) => (
              <button
                key={env}
                type="button"
                onClick={() => setActiveEnv(env)}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                  activeEnv === env
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {env}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {hasRepos ? (
            repos.map((repo) => {
              const ck   = entryKey(activeEnv, repo)
              const rows = getCellRows(activeEnv, repo)
              return (
                <div key={repo} className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">{repo}</p>
                  <VaultReadonlyTable cellKey={ck} rows={rows} revealed={revealed} onToggle={toggleReveal} />
                </div>
              )
            })
          ) : (
            <VaultReadonlyTable
              cellKey={entryKey(activeEnv, null)}
              rows={getCellRows(activeEnv, null)}
              revealed={revealed}
              onToggle={toggleReveal}
            />
          )}
        </div>
      </div>
    )
  }

  // ── edit mode ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Konfigurasi env & repo */}
      <div className="space-y-2 rounded-xl border border-border bg-muted/30 px-4 py-3">
        {/* Environments */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-24 shrink-0 text-xs font-medium text-muted-foreground">Environments</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {envs.map((env) => {
              const canRemove = !hasEntriesForEnv(entries, env)
              return (
                <span key={env} className="flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-0.5 text-xs">
                  {env}
                  <button
                    type="button"
                    onClick={() => removeEnv(env)}
                    disabled={!canRemove}
                    title={canRemove ? `Hapus ${env}` : 'Hapus semua variabel terlebih dahulu'}
                    className="ml-0.5 text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
                  >×</button>
                </span>
              )
            })}
            {addingEnv ? (
              <input
                ref={envInputRef}
                value={newEnvVal}
                onChange={(e) => setNewEnvVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addEnv() }
                  if (e.key === 'Escape') { setAddingEnv(false); setNewEnvVal('') }
                }}
                onBlur={() => { if (!newEnvVal.trim()) setAddingEnv(false) }}
                placeholder="nama env"
                className="h-6 w-24 rounded border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            ) : (
              <button
                type="button"
                onClick={() => setAddingEnv(true)}
                className="flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
              >
                + Tambah
              </button>
            )}
          </div>
        </div>

        {/* Services / Repos */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-24 shrink-0 text-xs font-medium text-muted-foreground">Services</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {repos.map((repo) => {
              const canRemove = !hasEntriesForRepo(entries, repo)
              return (
                <span key={repo} className="flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-0.5 text-xs">
                  {repo}
                  <button
                    type="button"
                    onClick={() => removeRepo(repo)}
                    disabled={!canRemove}
                    title={canRemove ? `Hapus ${repo}` : 'Hapus semua variabel terlebih dahulu'}
                    className="ml-0.5 text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
                  >×</button>
                </span>
              )
            })}
            {addingRepo ? (
              <input
                ref={repoInputRef}
                value={newRepoVal}
                onChange={(e) => setNewRepoVal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); addRepo() }
                  if (e.key === 'Escape') { setAddingRepo(false); setNewRepoVal('') }
                }}
                onBlur={() => { if (!newRepoVal.trim()) setAddingRepo(false) }}
                placeholder="nama service"
                className="h-6 w-28 rounded border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            ) : (
              <button
                type="button"
                onClick={() => setAddingRepo(true)}
                className="flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
              >
                + Tambah
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Env tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          {envs.map((env) => (
            <button
              key={env}
              type="button"
              onClick={() => setActiveEnv(env)}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                activeEnv === env
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {env}
            </button>
          ))}
        </div>
      </div>

      {/* Variabel per repo (atau langsung jika tidak ada repo) */}
      <div className="space-y-4">
        {hasRepos ? (
          repos.map((repo) => (
            <div key={repo} className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">{repo}</p>
              <VaultEditTable
                rows={getCellRows(activeEnv, repo)}
                onAdd={() => addVar(activeEnv, repo)}
                onUpdate={(i, f, v) => updateVar(activeEnv, repo, i, f, v)}
                onRemove={(i) => removeVar(activeEnv, repo, i)}
              />
            </div>
          ))
        ) : (
          <VaultEditTable
            rows={getCellRows(activeEnv, null)}
            onAdd={() => addVar(activeEnv, null)}
            onUpdate={(i, f, v) => updateVar(activeEnv, null, i, f, v)}
            onRemove={(i) => removeVar(activeEnv, null, i)}
          />
        )}
      </div>
    </div>
  )
}

// ─── sub-components ───────────────────────────────────────────────────────────

function VaultEditTable({
  rows,
  onAdd,
  onUpdate,
  onRemove,
}: {
  rows: VaultEntry[]
  onAdd: () => void
  onUpdate: (index: number, field: 'key' | 'value', val: string) => void
  onRemove: (index: number) => void
}) {
  return (
    <div className="space-y-1.5">
      {rows.length > 0 && (
        <div className="grid grid-cols-[2fr_3fr_auto] gap-1.5 px-0.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Key</span>
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Value</span>
          <span />
        </div>
      )}
      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-[2fr_3fr_auto] items-center gap-1.5">
          <input
            type="text"
            value={row.key}
            onChange={(e) => onUpdate(i, 'key', e.target.value)}
            placeholder="VARIABLE_NAME"
            className="h-8 rounded-md border border-input bg-background font-mono px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <input
            type="text"
            value={row.value}
            onChange={(e) => onUpdate(i, 'value', e.target.value)}
            placeholder="nilai"
            className="h-8 rounded-md border border-input bg-background font-mono px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            aria-label="Hapus variabel"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="mt-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
      >
        + Tambah Variabel
      </button>
    </div>
  )
}

function VaultReadonlyTable({
  cellKey,
  rows,
  revealed,
  onToggle,
}: {
  cellKey: string
  rows: VaultEntry[]
  revealed: Record<string, boolean>
  onToggle: (cellKey: string, index: number) => void
}) {
  if (rows.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[2fr_3fr_auto] gap-x-3 px-0.5">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Key</span>
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Value</span>
        <span />
      </div>
      {rows.map((row, i) => {
        const id = `${cellKey}_${i}`
        const show = revealed[id] ?? false
        return (
          <div key={i} className="grid grid-cols-[2fr_3fr_auto] items-center gap-x-3 rounded-md px-0.5 py-0.5">
            <span className="font-mono text-xs">{row.key || <span className="text-muted-foreground italic">—</span>}</span>
            <span className="font-mono text-xs">{show ? row.value : '••••••••'}</span>
            <button
              type="button"
              onClick={() => onToggle(cellKey, i)}
              className="text-[10px] text-muted-foreground underline hover:text-foreground"
            >
              {show ? 'Sembunyikan' : 'Tampilkan'}
            </button>
          </div>
        )
      })}
    </div>
  )
}
